import { ReceiptFilters } from './ReceiptFilters';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useData, post, useSession } from '@yingbaba/api';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Empty, ErrorNotice, Field, Modal, Notice, Status } from '@yingbaba/ui';
import {
  beijingTimestamp,
  datetime,
  money,
  paymentFactAmount,
  parseMemberMoney,
  memberCommandKey,
  clearMemberCommand,
} from '@yingbaba/utils';
import type { MemberPaymentMethod, MemberReceiptPage, PaidMemberReceipt } from '@yingbaba/types';

const states: Record<string, string> = {
  pending: '等待自动开通',
  review: '待财务核查',
  fulfilled: '已开通或排期',
  refunded: '已登记全额退款',
};
const reasons: Record<string, string> = {
  MEMBER_PAYMENT_MISMATCH: '到账金额或币种与订单不符',
  MEMBER_RECEIPT_TIME_INVALID: '付款时间缺失或不在支付有效期内',
  MEMBER_OWNER_INACTIVE: '归属主体已停用或注销中',
  MEMBER_ORDER_NOT_PAYABLE: '原订单已取消或结束',
  MEMBER_PLAN_CHANGE_NOT_SUPPORTED: '存在其他等级的有效会员',
  PROVIDER_REFUND_DETECTED: '渠道提示已发生退款，需核对凭证',
};
export function MemberReceipts() {
  const session = useSession();
  const role = session.data?.account.platform_role || '';
  const writable = ['super_admin', 'finance'].includes(role);
  const readable = writable || role === 'auditor';
  const [urlParams] = useSearchParams();
  const navigate = useNavigate();
  const page = Math.min(10000, Math.max(1, Math.floor(Number(urlParams.get('receipt_page')) || 1)));
  const status = Object.hasOwn(states, urlParams.get('receipt_status') || '')
    ? urlParams.get('receipt_status')!
    : '';
  const order = urlParams.get('receipt_order') || '';
  const [search, setSearch] = useState(order);
  useEffect(() => setSearch(order), [order]);
  useEffect(() => {
    if (readable && window.location.hash === '#member-receipts')
      document.getElementById('member-receipts')?.scrollIntoView({ block: 'start' });
  }, [readable]);
  function update(values: Record<string, string>) {
    const next = new URLSearchParams(urlParams);
    next.set('tab', 'receipts');
    for (const [key, value] of Object.entries(values)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    navigate({ search: next.toString(), hash: '#member-receipts' }, { preventScrollReset: true });
  }
  const setPage = (value: number) => update({ receipt_page: String(value) });
  const setStatus = (value: string) => update({ receipt_status: value, receipt_page: '' });
  const params = new URLSearchParams({
    page: String(page),
    ...(status ? { status } : {}),
    ...(order ? { order_id: order } : {}),
  });
  const q = useData<MemberReceiptPage>(`/admin/member/receipts?${params}`, readable, 30_000);
  const methods = useData<MemberPaymentMethod[]>('/member/payment-methods', readable);
  const [action, setAction] = useState<{
    kind: 'retry' | 'refund';
    receipt: PaidMemberReceipt;
  } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const qc = useQueryClient();
  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!action || pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(null);
    const f = new FormData(e.currentTarget);
    try {
      const reason = String(f.get('reason') || '').trim();
      if (action.kind === 'retry') {
        await post(`/admin/member/receipts/${action.receipt.id}/retry`, { reason });
      } else {
        if (f.get('verified') !== 'yes') throw new Error('请先核对渠道真实退款凭证。');
        const payload = {
          reference: String(f.get('reference') || '').trim(),
          amount_minor: parseMemberMoney(String(f.get('amount'))),
          currency: 'CNY',
          refunded_at: beijingTimestamp(String(f.get('refunded_at'))),
          reason,
        };
        const scope = `receipt-refund:${session.data?.account.id}:${action.receipt.id}`;
        const idempotency_key = await memberCommandKey(scope, payload);
        await post(`/admin/member/receipts/${action.receipt.id}/refund-record`, {
          ...payload,
          idempotency_key,
        });
        clearMemberCommand(scope);
      }
      setAction(null);
      await qc.invalidateQueries();
    } catch (e) {
      setError(e);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  if (!readable) return null;
  return (
    <section id="member-receipts" className="admin-data-panel">
      <div className="admin-section-heading">
        <div>
          <h2>会员收款与退款</h2>
          <p className="muted">核实到账、处理开通异常，并登记渠道已完成的退款。</p>
        </div>
      </div>
      <p className="muted">
        {methods.data?.some((m) => m.available)
          ? '已开放：' +
            methods.data
              .filter((m) => m.available)
              .map((m) => m.name)
              .join('、')
          : '新线上收款未开放；在途支付仍可继续核验。'}
      </p>
      <ReceiptFilters
        states={states}
        status={status}
        onStatus={setStatus}
        statusLabel="线上到账状态"
        orderLabel="查询会员订单号"
        search={search}
        onSearch={setSearch}
        onSubmit={(e) => {
          e.preventDefault();
          if (search.trim() && !/^[0-9A-HJKMNP-TV-Z]{26}$/.test(search.trim())) {
            setError(new Error('请填写完整的订单编号'));
            return;
          }
          setError(null);
          update({ receipt_order: search.trim(), receipt_page: '' });
        }}
      />
      {!action && <ErrorNotice error={error} />}
      <ErrorNotice error={q.error} />
      {q.isLoading ? (
        <p>正在加载线上到账记录…</p>
      ) : (
        q.isSuccess && (
          <>
            {(q.data.items || []).length ? (
              q.data.items.map((r) => (
                <div className="list-item admin-record" key={r.id}>
                  <div className="grow" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    <div className="admin-record-heading">
                      <strong>{paymentFactAmount(r.amount_minor, r.currency)}</strong>
                      <Status
                        value={
                          r.fulfillment_status === 'review'
                            ? 'pending'
                            : r.fulfillment_status === 'fulfilled'
                              ? 'completed'
                              : r.fulfillment_status
                        }
                        label={states[r.fulfillment_status] || r.fulfillment_status}
                      />
                    </div>
                    <dl className="admin-record-meta">
                      <div>
                        <dt>支付渠道</dt>
                        <dd>{r.provider === 'wechat' ? '微信支付' : '支付宝'}</dd>
                      </div>
                      <div>
                        <dt>订单编号</dt>
                        <dd>{r.order_id}</dd>
                      </div>
                      <div>
                        <dt>渠道流水</dt>
                        <dd>{r.trade_no}</dd>
                      </div>
                      <div>
                        <dt>实际付款</dt>
                        <dd>{r.paid_at ? datetime(r.paid_at) : '待渠道补充时间'}</dd>
                      </div>
                    </dl>
                    {r.currency !== 'CNY' && (
                      <p>到账币种异常，请通过原渠道核实；此处仅支持人民币退款登记。</p>
                    )}
                    {r.failure_code && (
                      <p>{reasons[r.failure_code] || `需要核查：${r.failure_code}`}</p>
                    )}
                    {r.refund_reference && (
                      <p>
                        全额退款凭证：{r.refund_reference} · {datetime(r.refunded_at || '')}
                      </p>
                    )}
                  </div>
                  <div className="admin-record-actions">
                    {writable && ['pending', 'review'].includes(r.fulfillment_status) && (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setError(null);
                          setAction({ kind: 'retry', receipt: r });
                        }}
                      >
                        复核开通条件
                      </Button>
                    )}
                    {writable && r.currency === 'CNY' && r.fulfillment_status !== 'refunded' && (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setError(null);
                          setAction({ kind: 'refund', receipt: r });
                        }}
                      >
                        登记已完成退款
                      </Button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <Empty title="暂无匹配的线上到账记录" />
            )}
          </>
        )
      )}
      <div className="admin-pagination">
        <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
          上一页到账
        </Button>
        <span>
          第 {page} 页 · 共 {q.data?.total || 0} 条
        </span>
        <Button
          variant="secondary"
          disabled={!q.data || page * q.data.page_size >= q.data.total}
          onClick={() => setPage(page + 1)}
        >
          下一页到账
        </Button>
      </div>
      {action && (
        <Modal
          open
          busy={busy}
          title={action.kind === 'retry' ? '复核原订单开通条件' : '登记渠道已完成的全额退款'}
          onOpenChange={(open) => {
            if (!open && !busy) setAction(null);
          }}
          footer={
            <Button type="submit" form="admin-member-receipt-dialog" loading={busy}>
              {action.kind === 'refund' ? '确认登记已退款' : '提交复核'}
            </Button>
          }
        >
          <p>订单：{action.receipt.order_id}</p>
          <p>实际到账：{paymentFactAmount(action.receipt.amount_minor, action.receipt.currency)}</p>
          <Notice>
            {action.kind === 'retry'
              ? '仅重新检查原金额、付款时间、归属和套餐条件，不能强制通过错误款项。复核后仍不符合条件的记录会继续显示待核查。'
              : '这不是退款按钮，不会向渠道发起转账。必须已经在渠道完成全额退款并核实凭证；登记后只停止这笔支付对应的服务周期，保留其他续费与赠送。'}
          </Notice>
          <form
            id="admin-member-receipt-dialog"
            onSubmit={submit}
            style={{ display: 'grid', gap: 12 }}
          >
            {action.kind === 'refund' && (
              <>
                <Field label="真实退款流水">
                  <input
                    className="input"
                    aria-label="真实退款流水"
                    name="reference"
                    required
                    minLength={6}
                    maxLength={120}
                  />
                </Field>
                <Field label="已退款金额（元）">
                  <input
                    className="input"
                    aria-label="已退款金额（元）"
                    name="amount"
                    required
                    inputMode="decimal"
                    defaultValue={(action.receipt.amount_minor / 100).toFixed(2)}
                  />
                </Field>
                <Field label="退款完成时间（北京时间）">
                  <input
                    className="input"
                    aria-label="退款完成时间（北京时间）"
                    name="refunded_at"
                    type="datetime-local"
                    step="1"
                    required
                  />
                </Field>
                <label>
                  <input type="checkbox" name="verified" value="yes" required />{' '}
                  已独立核实真实全额退款凭证
                </label>
              </>
            )}
            <Field label="核查依据">
              <textarea
                aria-label="核查依据"
                name="reason"
                required
                minLength={6}
                maxLength={500}
              />
            </Field>
            <ErrorNotice error={error} />
          </form>
        </Modal>
      )}
    </section>
  );
}
